/**
 * InfoTooltip — issue #997 / #999.
 *
 * A small `?` icon that reveals a hover popover with multi-line
 * explanatory copy. Used on the structural dashboard health-strip
 * bars and the stitch review summary cards where project-internal
 * vocabulary needs a definition.
 *
 * Pure CSS hover — no JS state, no portal, no positioning library.
 * The popover is anchored above the icon and clipped only by the
 * parent's overflow. For dense layouts where the popover risks
 * clipping, set `align="right"` to flip it.
 *
 * Out of scope: keyboard focus support (icon is a `<span>` not a
 * `<button>`), longer-form rich content (use a Dialog if needed),
 * pointer events on the popover itself (it's `pointer-events-none`).
 */

import type { ReactNode } from 'react';

export function InfoTooltip({
  children,
  align = 'left',
  className,
}: {
  children: ReactNode;
  align?: 'left' | 'right' | 'center';
  className?: string;
}) {
  // Position the popover above the icon. Horizontal alignment:
  //   left   → popover's left edge aligns with icon's left edge
  //   right  → popover's right edge aligns with icon's right edge
  //   center → popover is centered above the icon
  const alignClasses: Record<typeof align, string> = {
    left: 'left-0',
    right: 'right-0',
    center: 'left-1/2 -translate-x-1/2',
  };
  return (
    <span
      data-testid="info-tooltip"
      className={`relative inline-flex items-center group cursor-help ${className ?? ''}`}
    >
      <svg
        className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="9" strokeWidth={2} />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8h.01M11 12h1v4h1"
        />
      </svg>
      <span
        role="tooltip"
        className={`pointer-events-none absolute bottom-full mb-2 z-50 hidden group-hover:block w-72 p-3 text-xs leading-relaxed text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg ${alignClasses[align]}`}
      >
        {children}
      </span>
    </span>
  );
}
